import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Bird,
  Bot,
  Check,
  CircleAlert,
  ClipboardPen,
  Dumbbell,
  Eye,
  Loader2,
  MousePointer2,
  PartyPopper,
  Rocket,
  ScanSearch,
  Sparkles,
} from 'lucide-react';
import { estimatePageOnlySeconds } from '../utils/timeEstimate';

type MiniScanPhase = 'travel' | 'draw' | 'reading' | 'done' | 'poof';
type MiniScanBox = { x: number; y: number; w: number; h: number };
type PageDetectionDot = { x: number; y: number };

const MINI_DRAW_MS = 620;
const MINI_TRAVEL_MS = 320;
const MINI_READING_MS = 820;
const MINI_DONE_MS = 220;
const MINI_POOF_MS = 220;

const miniRandom = (min: number, max: number) => min + Math.random() * (max - min);
const seededUnit = (seed: number) => {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};
const createMiniScanBox = (): MiniScanBox => {
  const w = miniRandom(26, 54);
  const h = miniRandom(18, 40);
  const x = miniRandom(4, 100 - w - 4);
  const y = miniRandom(6, 100 - h - 6);
  return { x, y, w, h };
};

const isReceptacleComponentName = (componentName: unknown) => {
  const name = String(componentName || '').toLowerCase();
  return name === '__receptacles_group__' || name.includes('receptacle') || name.includes('duplex');
};

const formatReceptacleClassLabel = (name: string) => {
  if (!name) return '';
  return name
    .replace(/Halfswitch Specific Circuit Receptacle/gi, 'Halfswitch Receptacle')
    .replace(/^(wall|ceiling|floor|furniture)\s+mounted\s+/i, '')
    .trim();
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getLightingThumbnailScale = (prediction: any, isLighting: boolean): number => {
  if (!isLighting) return 1;

  const relWidth = Number(prediction?.relWidth);
  const relHeight = Number(prediction?.relHeight);
  if (!Number.isFinite(relWidth) || !Number.isFinite(relHeight)) return 1.8;

  const longestSide = Math.max(relWidth, relHeight);
  if (!(longestSide > 0)) return 1.8;

  // Smaller bboxes should get more zoom so labels stay readable.
  return clamp(0.12 / Math.max(longestSide, 0.003), 1.4, 6);
};

const MiniPageScanOverlay: React.FC<{
  animate: boolean;
  delayMs: number;
  tone: 'lighting' | 'power';
  motionSeed: number;
}> = ({ animate, delayMs, tone, motionSeed }) => {
  const [scanBox, setScanBox] = useState<MiniScanBox | null>(null);
  const [phase, setPhase] = useState<MiniScanPhase>('travel');
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number }>({ x: 10, y: 10 });
  const [cursorTransitionMs, setCursorTransitionMs] = useState(0);

  const travelMs = Math.round(MINI_TRAVEL_MS + seededUnit(motionSeed + 11) * 170);
  const drawMs = Math.round(MINI_DRAW_MS + seededUnit(motionSeed + 23) * 180);
  const readingMs = Math.round(MINI_READING_MS + seededUnit(motionSeed + 37) * 260);
  const doneMs = Math.round(MINI_DONE_MS + seededUnit(motionSeed + 41) * 80);
  const poofMs = Math.round(MINI_POOF_MS + seededUnit(motionSeed + 53) * 90);
  const initialDelayMs = delayMs + Math.round(seededUnit(motionSeed + 71) * 420);

  useEffect(() => {
    if (!animate) {
      setScanBox(null);
      setPhase('travel');
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const schedule = (ms: number, callback: () => void) => {
      timeoutId = setTimeout(() => {
        if (!cancelled) callback();
      }, ms);
    };

    const runCycle = () => {
      const next = createMiniScanBox();
      const nextStart = { x: next.x, y: next.y };
      const nextEnd = { x: next.x + next.w, y: next.y + next.h };

      setScanBox(null);
      setPhase('travel');
      setCursorTransitionMs(travelMs);
      setCursorPos(nextStart);

      schedule(travelMs, () => {
        setScanBox(next);
        setPhase('draw');
        setCursorTransitionMs(drawMs);
        setCursorPos(nextEnd);

        schedule(drawMs, () => {
          setPhase('reading');
          schedule(readingMs, () => {
            setPhase('done');
            schedule(doneMs, () => {
              setPhase('poof');
              schedule(poofMs, runCycle);
            });
          });
        });
      });
    };

    schedule(initialDelayMs, runCycle);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [animate, delayMs, doneMs, drawMs, initialDelayMs, poofMs, readingMs, travelMs]);

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(8, 12, 20, 0.32)' }} />

      {scanBox && (
        <div
          style={{
            position: 'absolute',
            left: `${scanBox.x}%`,
            top: `${scanBox.y}%`,
            width: `${scanBox.w}%`,
            height: `${scanBox.h}%`,
            borderRadius: 8,
            border: '2px solid #ffffff',
            background: 'transparent',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.24) inset, 0 0 12px rgba(255,255,255,0.32), 0 0 30px rgba(0,0,0,0.34)',
            transformOrigin: 'top left',
            transform: phase === 'draw' ? 'scale(0)' : 'scale(1)',
            animation: phase === 'draw'
              ? `miniScanDragHole ${drawMs}ms linear forwards`
              : phase === 'poof'
                ? `miniScanPoof ${poofMs}ms ease-out forwards`
                : undefined,
          }}
        >
          {(phase === 'reading' || phase === 'done') && (
            <>
              <div
                style={{
                  position: 'absolute',
                  inset: 2,
                  borderRadius: 6,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 70%, rgba(255,255,255,0.02) 100%)',
                  backdropFilter: 'brightness(1.22)',
                  WebkitBackdropFilter: 'brightness(1.22)',
                  clipPath: phase === 'done' ? 'inset(0 0 0% 0)' : 'inset(0 0 100% 0)',
                  animation: phase === 'done' ? 'none' : `miniScanFill ${Math.max(420, readingMs - 80)}ms linear forwards`,
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: 4,
                  right: 4,
                  height: 2,
                  borderRadius: 999,
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.15) 100%)',
                  boxShadow: '0 0 10px rgba(255,255,255,1), 0 0 24px rgba(255,255,255,0.95), 0 0 44px rgba(255,255,255,0.75)',
                  top: phase === 'done' ? 'calc(100% - 2px)' : undefined,
                  animation: phase === 'done' ? 'none' : `miniScanSweep ${Math.max(420, readingMs - 80)}ms linear forwards`,
                  pointerEvents: 'none',
                }}
              />
            </>
          )}

          {phase === 'done' && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: '#bfdbfe',
                textShadow: '0 0 6px rgba(41, 66, 96, 0.8), 0 0 14px rgba(191, 219, 254, 0.5)',
                background: 'rgba(0, 0, 0, 0.41)',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(191, 219, 254, 0.4)',
                whiteSpace: 'nowrap',
                letterSpacing: '0.02em',
              }}
            >
              <Check size={11} color="#bfdbfe" strokeWidth={3} style={{ filter: 'drop-shadow(0 0 5px rgba(191, 219, 254, 0.7))' }} />
              <span>Scanned</span>
            </div>
          )}

          {/* Cursor anchored to bottom-right during draw — rides the scale animation */}
          {phase === 'draw' && (
            <div
              style={{
                position: 'absolute',
                right: -26,
                bottom: -26,
                width: 30,
                height: 30,
                filter: 'drop-shadow(0 1px 5px rgba(0,0,0,0.65))',
                pointerEvents: 'none',
              }}
            >
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <MousePointer2 size={28} color="#000000" strokeWidth={3.8} style={{ position: 'absolute', left: 0, top: 0 }} />
                <MousePointer2
                  size={28}
                  color={tone === 'lighting' ? '#f6b73c' : '#22c55e'}
                  strokeWidth={2.7}
                  style={{ position: 'absolute', left: 0, top: 0 }}
                />
                <MousePointer2
                  size={28}
                  color={tone === 'lighting' ? '#fde68a' : '#86efac'}
                  strokeWidth={2.1}
                  style={{ position: 'absolute', left: 0, top: 0, clipPath: 'polygon(0 0, 100% 0, 68% 100%, 0 100%)' }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cursor for travel + non-draw phases — independent positioning */}
      {animate && phase !== 'draw' && (
        <div
          style={{
            position: 'absolute',
            left: `${cursorPos.x}%`,
            top: `${cursorPos.y}%`,
            width: 30,
            height: 30,
            transform: 'translate(-22%, -14%)',
            transition: `left ${cursorTransitionMs}ms linear, top ${cursorTransitionMs}ms linear`,
            filter: 'drop-shadow(0 1px 5px rgba(0,0,0,0.65))',
          }}
        >
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <MousePointer2 size={28} color="#000000" strokeWidth={3.8} style={{ position: 'absolute', left: 0, top: 0 }} />
            <MousePointer2
              size={28}
              color={tone === 'lighting' ? '#f6b73c' : '#22c55e'}
              strokeWidth={2.7}
              style={{ position: 'absolute', left: 0, top: 0 }}
            />
            <MousePointer2
              size={28}
              color={tone === 'lighting' ? '#fde68a' : '#86efac'}
              strokeWidth={2.1}
              style={{ position: 'absolute', left: 0, top: 0, clipPath: 'polygon(0 0, 100% 0, 68% 100%, 0 100%)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const MiniPageDetectionDotsOverlay: React.FC<{
  dots: PageDetectionDot[];
  show: boolean;
  tone: 'lighting' | 'power';
}> = ({ dots, show, tone }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let rafId: number | null = null;

    const draw = (nowMs: number) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      const cssWidth = Math.max(1, Math.floor(rect.width));
      const cssHeight = Math.max(1, Math.floor(rect.height));
      const dpr = Math.max(1, window.devicePixelRatio || 1);

      const targetWidth = Math.floor(cssWidth * dpr);
      const targetHeight = Math.floor(cssHeight * dpr);
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
      }

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!show || dots.length === 0) return;

      ctx.scale(dpr, dpr);
      ctx.fillStyle = tone === 'lighting' ? 'rgba(250, 204, 21, 0.95)' : 'rgba(34, 197, 94, 0.95)';
      ctx.strokeStyle = tone === 'lighting' ? 'rgba(254, 240, 138, 0.98)' : 'rgba(187, 247, 208, 0.98)';
      ctx.lineWidth = 0.9;

      for (const dot of dots) {
        const x = dot.x * cssWidth;
        const y = dot.y * cssHeight;
        ctx.beginPath();
        ctx.arc(x, y, 2.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }

      // Pulse outward subtle white orbs to make filtering feel alive.
      const pulseCap = 180;
      const pulseStep = dots.length > pulseCap ? Math.ceil(dots.length / pulseCap) : 1;
      const pulseDots = pulseStep === 1 ? dots : dots.filter((_, idx) => idx % pulseStep === 0);
      const timeSec = nowMs / 1000;

      for (let idx = 0; idx < pulseDots.length; idx += 1) {
        const dot = pulseDots[idx];
        const phase = (timeSec * 1.55 + idx * 0.097) % 1;
        const rippleRadius = 2.4 + phase * 7.2;
        const alpha = Math.max(0, 0.45 - phase * 0.45);

        const x = dot.x * cssWidth;
        const y = dot.y * cssHeight;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
        ctx.lineWidth = 0.95;
        ctx.arc(x, y, rippleRadius, 0, Math.PI * 2);
        ctx.stroke();
      }
    };

    const animate = (ts: number) => {
      draw(ts);
      if (show && dots.length > 0) {
        rafId = requestAnimationFrame(animate);
      }
    };

    draw(performance.now());
    if (show && dots.length > 0) {
      rafId = requestAnimationFrame(animate);
    }

    const onResize = () => draw(performance.now());
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [dots, show, tone]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
};

type ZoomTarget = { relX: number; relY: number; thumbnail: string };

const ZOOM_IN_MS = 650;
const ZOOM_HOLD_MS = 850;
const ZOOM_OUT_MS = 550;
const ZOOM_PAUSE_MS = 300;
const ZOOM_SCALE = 4.5;

const useZoomShowcase = (active: boolean, detectionCount: number) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [phase, setPhase] = useState<'dots' | 'zooming-in' | 'hold' | 'zooming-out'>('dots');

  useEffect(() => {
    if (!active || detectionCount === 0) {
      setPhase('dots');
      setCurrentIdx(0);
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const schedule = (ms: number, cb: () => void) => {
      timeoutId = setTimeout(() => { if (!cancelled) cb(); }, ms);
    };

    const runCycle = (idx: number) => {
      setCurrentIdx(idx % detectionCount);
      setPhase('zooming-in');

      schedule(ZOOM_IN_MS, () => {
        setPhase('hold');
        schedule(ZOOM_HOLD_MS, () => {
          setPhase('zooming-out');
          schedule(ZOOM_OUT_MS, () => {
            setPhase('dots');
            schedule(ZOOM_PAUSE_MS, () => {
              runCycle(idx + 1);
            });
          });
        });
      });
    };

    schedule(600, () => runCycle(0));

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [active, detectionCount]);

  return { currentIdx, phase };
};

const BLUEPRINT_PLACEHOLDER_COUNT = 6;
const blueprintPlaceholderUrl = (pageIdx: number) =>
  `/blueprint-placeholders/bp-${pageIdx % BLUEPRINT_PLACEHOLDER_COUNT}.svg`;

const CctvTile: React.FC<{
  page: number | null;
  pageIdx: number;
  thumbnail: string | undefined;
  dots: PageDetectionDot[];
  zoomTargets: ZoomTarget[];
  isProcessing: boolean;
  hasDetections: boolean;
  tone: 'lighting' | 'power';
}> = ({ page, pageIdx, thumbnail, dots, zoomTargets, isProcessing, hasDetections, tone }) => {
  const hasFeed = page != null;
  const thumbLoaded = hasFeed && !!thumbnail;
  const shouldAnimateMiniScan = hasFeed && isProcessing && !hasDetections;
  const doZoom = thumbLoaded && hasDetections && isProcessing && zoomTargets.length > 0;

  const { currentIdx, phase: zoomPhase } = useZoomShowcase(doZoom, zoomTargets.length);

  const activeDet = doZoom ? zoomTargets[currentIdx % zoomTargets.length] : null;
  const originX = activeDet ? Math.max(5, Math.min(95, activeDet.relX * 100)) : 50;
  const originY = activeDet ? Math.max(5, Math.min(95, activeDet.relY * 100)) : 50;

  const isZoomed = zoomPhase === 'zooming-in' || zoomPhase === 'hold';
  const zoomScale = isZoomed ? ZOOM_SCALE : 1;
  const transitionMs =
    zoomPhase === 'zooming-in' ? ZOOM_IN_MS
    : zoomPhase === 'zooming-out' ? ZOOM_OUT_MS
    : 0;

  const borderColor = tone === 'lighting' ? 'rgba(250, 204, 21, 0.9)' : 'rgba(34, 197, 94, 0.9)';
  const glowColor = tone === 'lighting' ? 'rgba(250, 204, 21, 0.5)' : 'rgba(34, 197, 94, 0.5)';

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: '#0f172a',
        width: '100%',
        height: '100%',
      }}
    >
      {/* Zoomable content layer — image + dots zoom together */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transformOrigin: `${originX}% ${originY}%`,
          transform: `scale(${zoomScale})`,
          transition: transitionMs > 0
            ? `transform ${transitionMs}ms cubic-bezier(0.4, 0, 0.2, 1), transform-origin 0ms`
            : 'none',
        }}
      >
        {thumbLoaded ? (
          <img
            src={thumbnail}
            alt={`Page ${page} preview`}
            style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
          />
        ) : (
          <img
            src={blueprintPlaceholderUrl(pageIdx)}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
          />
        )}

        {/* Dim overlay — always on */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(8, 12, 20, 0.32)', pointerEvents: 'none' }} />

        {hasFeed && (
          <MiniPageScanOverlay
            animate={shouldAnimateMiniScan}
            delayMs={pageIdx * 220}
            tone={tone}
            motionSeed={(page ?? 0) * 97 + pageIdx * 17}
          />
        )}

        {hasFeed && dots.length > 0 && (
          <MiniPageDetectionDotsOverlay
            dots={dots}
            show
            tone={tone}
          />
        )}
      </div>

      {/* Detection thumbnail card — shown during zoom-in + hold, positioned at the detection's location */}
      {doZoom && (zoomPhase === 'zooming-in' || zoomPhase === 'hold') && activeDet?.thumbnail && (
        <div
          style={{
            position: 'absolute',
            left: `${originX}%`,
            top: `${originY}%`,
            transform: 'translate(-50%, -50%)',
            borderRadius: '10px',
            border: `2px solid ${borderColor}`,
            boxShadow: `0 0 20px ${glowColor}, 0 0 40px ${glowColor}, 0 4px 16px rgba(0,0,0,0.5)`,
            overflow: 'hidden',
            background: '#000',
            animation: 'zoomShowcasePopIn 280ms ease-out',
            zIndex: 3,
            width: '40%',
            maxWidth: '180px',
            aspectRatio: '4 / 3',
          }}
        >
          <img
            src={activeDet.thumbnail}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}

      {/* Page badge — outside zoom so it stays readable */}
      {hasFeed && (
        <div
          style={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            borderRadius: 999,
            padding: '3px 8px',
            fontSize: 10,
            fontWeight: 700,
            color: 'rgba(255,255,255,0.88)',
            background: 'rgba(0, 0, 0, 0.52)',
            border: '1px solid rgba(255,255,255,0.22)',
            zIndex: 4,
          }}
        >
          Page {page}{dots.length > 0 ? ` · ${dots.length} found` : ''}
        </div>
      )}
    </div>
  );
};

export interface MultiComponentReviewModalProps {
  show: boolean;
  isCountingAll: boolean;
  multiComponentDetections: any[];
  currentReviewComponentIndex: number;
  allComponentSelections: Map<string, Set<string>>;
  setAllComponentSelections: React.Dispatch<React.SetStateAction<Map<string, Set<string>>>>;
  receptacleGroupComponents: Array<{ name: string; color: string; referenceImage?: string }>;
  showReceptacleRefPopup: boolean;
  setShowReceptacleRefPopup: (show: boolean) => void;
  countAllProgress: string;
  countAllComponentProgress: Array<any>;
  componentPagesByName: Map<string, number[]>;
  pageThumbnailsByPage: Map<number, string>;
  detectionClassifications: Map<string, string>;
  setDetectionClassifications: React.Dispatch<React.SetStateAction<Map<string, string>>>;
  receptacleClassFilter: string;
  setReceptacleClassFilter: (v: string) => void;
  teleDataGroupComponents: Array<{ name: string; color: string; referenceImage?: string }>;
  teleDataClassFilter: string;
  setTeleDataClassFilter: (v: string) => void;
  lightingGroupComponents: Array<{ name: string; symbol: string; color: string }>;
  lightingClassFilter: string;
  setLightingClassFilter: (v: string) => void;
  bulkSelectedDetections: Set<string>;
  setBulkSelectedDetections: React.Dispatch<React.SetStateAction<Set<string>>>;
  bulkReclassTarget: string;
  setBulkReclassTarget: (v: string) => void;
  toggleMultiDetectionSelection: (componentName: string, detectionId: string) => void;
  handleMultiComponentBack: () => void;
  handleFinishMultiComponentReview: () => void;
  handleExportDatasetZip: () => void;
  handleMultiComponentNext: () => void;
  isExportingDataset: boolean;
  datasetExportError: string | null;
  handleCancel: () => void;
  isParallelCountSession: boolean;
  pendingKeyComponents: any[];
  isFinalizingReview: boolean;
  reviewFinalizeError: string | null;
  pageNumber: number;
}

export const MultiComponentReviewModal: React.FC<MultiComponentReviewModalProps> = (props) => {
  const {
    show,
    isCountingAll,
    multiComponentDetections,
    currentReviewComponentIndex,
    allComponentSelections,
    setAllComponentSelections,
    receptacleGroupComponents,
    showReceptacleRefPopup,
    setShowReceptacleRefPopup,
    countAllProgress,
    countAllComponentProgress,
    componentPagesByName,
    pageThumbnailsByPage,
    detectionClassifications,
    lightingGroupComponents,
    toggleMultiDetectionSelection,
    handleMultiComponentBack,
    handleFinishMultiComponentReview,
    handleExportDatasetZip,
    handleMultiComponentNext,
    isExportingDataset,
    datasetExportError,
    handleCancel,
    isParallelCountSession,
    pendingKeyComponents,
    isFinalizingReview,
    reviewFinalizeError,
    pageNumber,
  } = props;

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isCountingAll) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isCountingAll]);

  const currentComponent = multiComponentDetections[currentReviewComponentIndex];
  const currentSelections = currentComponent ? (allComponentSelections.get(currentComponent.componentName) || new Set()) : new Set();
  const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
  const isLastComponent = currentReviewComponentIndex === multiComponentDetections.length - 1;
  const isLightingReview = Boolean(currentComponent?.isLighting || currentComponent?.componentName === '__LIGHTING_GROUP__');
  const selectedBorderColor = isLightingReview
    ? '#eab308'
    : (currentComponent?.componentColor || 'var(--blue)');

  const anyPowerStillProcessing = useMemo(() => {
    if (!isParallelCountSession || !isCountingAll) return false;
    return multiComponentDetections.some((comp: any, idx: number) => {
      if (comp.isLighting) return false;
      const prog = countAllComponentProgress[idx];
      return prog?.status === 'processing';
    });
  }, [isParallelCountSession, isCountingAll, multiComponentDetections, countAllComponentProgress]);

  const isUnifiedPowerCctv = isParallelCountSession && anyPowerStillProcessing && !isLightingReview;

  const unifiedPowerPages = useMemo(() => {
    if (!isUnifiedPowerCctv) return [] as number[];
    const pageSet = new Set<number>();
    Array.from(componentPagesByName.entries()).forEach(([key, pages]) => {
      const comp = multiComponentDetections.find((c: any) => c.componentName === key);
      if (comp?.isLighting) return;
      pages.forEach((p: number) => pageSet.add(p));
    });
    return Array.from(pageSet).sort((a, b) => a - b).slice(0, 4);
  }, [isUnifiedPowerCctv, componentPagesByName, multiComponentDetections]);

  const currentComponentPages = currentComponent
    ? Array.from(new Set(componentPagesByName.get(currentComponent.componentName) || [])).sort((a, b) => a - b)
    : [];

  const previewPages = isUnifiedPowerCctv
    ? unifiedPowerPages
    : (
        currentComponentPages.length > 0
          ? currentComponentPages
          : (currentProgress?.currentPage ? [currentProgress.currentPage] : [])
      ).slice(0, 4);

  const pageCount = previewPages.length;
  const previewTone: 'lighting' | 'power' = isLightingReview ? 'lighting' : 'power';

  const allPowerDetections = useMemo(() => {
    if (!isUnifiedPowerCctv) return [];
    const dets: Array<{ prediction: any; thumbnail: string }> = [];
    for (const comp of multiComponentDetections) {
      if (comp.isLighting) continue;
      if (!Array.isArray(comp.detections)) continue;
      dets.push(...comp.detections);
    }
    return dets;
  }, [isUnifiedPowerCctv, multiComponentDetections]);

  const detectionSource = isUnifiedPowerCctv
    ? allPowerDetections
    : (currentComponent?.detections || []);

  const hasAnyDetectionDots = detectionSource.length > 0;
  const isCurrentProcessing = isUnifiedPowerCctv
    ? true
    : (isCountingAll && currentProgress?.status === 'processing');
  const showLiveScanPreview = isUnifiedPowerCctv || isCurrentProcessing || !currentComponent;
  const shouldShowFullBleedPreview = showLiveScanPreview && isCountingAll && previewPages.length > 0;

  const pageDetectionDots = useMemo(() => {
    const grouped = new Map<number, PageDetectionDot[]>();
    for (const det of detectionSource) {
      const page = Number(det?.prediction?.page);
      const relX = Number(det?.prediction?.relX);
      const relY = Number(det?.prediction?.relY);
      if (!Number.isFinite(page) || page <= 0) continue;
      if (!Number.isFinite(relX) || !Number.isFinite(relY)) continue;
      if (relX < 0 || relX > 1 || relY < 0 || relY > 1) continue;

      if (!grouped.has(page)) grouped.set(page, []);
      grouped.get(page)!.push({ x: relX, y: relY });
    }

    return grouped;
  }, [detectionSource]);

  const pageZoomTargets = useMemo(() => {
    const grouped = new Map<number, ZoomTarget[]>();
    for (const det of detectionSource) {
      const page = Number(det?.prediction?.page);
      const relX = Number(det?.prediction?.relX);
      const relY = Number(det?.prediction?.relY);
      if (!Number.isFinite(page) || page <= 0) continue;
      if (!Number.isFinite(relX) || !Number.isFinite(relY)) continue;
      if (relX < 0 || relX > 1 || relY < 0 || relY > 1) continue;

      if (!grouped.has(page)) grouped.set(page, []);
      grouped.get(page)!.push({ relX, relY, thumbnail: det.thumbnail || '' });
    }

    return grouped;
  }, [detectionSource]);

  const totalApprovedCredits = multiComponentDetections.reduce((sum: number, comp: any) => {
    const selections = allComponentSelections.get(comp.componentName) || new Set();
    return sum + selections.size;
  }, 0);
  const totalDetectionsForExport = multiComponentDetections.reduce((sum: number, comp: any) => {
    const count = Array.isArray(comp?.detections) ? comp.detections.length : 0;
    return sum + count;
  }, 0);

  if (!show || (!isCountingAll && multiComponentDetections.length === 0)) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100006,
        padding: '20px'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          background: shouldShowFullBleedPreview
            ? 'rgba(11, 18, 32, 0.86)'
            : 'rgba(255, 255, 255, 0.17)',
          borderRadius: '16px',
          padding: shouldShowFullBleedPreview ? '0' : '28px',
          width: '95vw',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.45)',
          border: '1px solid rgba(255, 255, 255, 0.9)',
          position: 'relative',
          overflow: 'hidden',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ===== FULL-BLEED CCTV PREVIEW MODE ===== */}
        {shouldShowFullBleedPreview && (
          <>
            {/* CCTV grid as full modal background — adaptive layout */}
            {pageCount === 1 && (
              <div style={{ position: 'absolute', inset: 0, background: '#0b1220' }}>
                <CctvTile
                  page={previewPages[0]}
                  pageIdx={0}
                  thumbnail={pageThumbnailsByPage.get(previewPages[0])}
                  dots={pageDetectionDots.get(previewPages[0]) || []}
                  zoomTargets={pageZoomTargets.get(previewPages[0]) || []}
                  isProcessing={isUnifiedPowerCctv || currentProgress?.status === 'processing'}
                  hasDetections={hasAnyDetectionDots}
                  tone={previewTone}
                />
              </div>
            )}
            {pageCount === 2 && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gridTemplateRows: '1fr',
                  gap: '0',
                  background: '#0b1220',
                }}
              >
                {previewPages.map((page, pageIdx) => (
                  <CctvTile
                    key={`cctv-${pageIdx}-${page}`}
                    page={page}
                    pageIdx={pageIdx}
                    thumbnail={pageThumbnailsByPage.get(page)}
                    dots={pageDetectionDots.get(page) || []}
                    zoomTargets={pageZoomTargets.get(page) || []}
                    isProcessing={isUnifiedPowerCctv || currentProgress?.status === 'processing'}
                    hasDetections={hasAnyDetectionDots}
                    tone={previewTone}
                  />
                ))}
              </div>
            )}
            {pageCount === 3 && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'rgba(0, 0, 0, 0)',
                }}
              >
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 0 }}>
                  {previewPages.slice(0, 2).map((page, pageIdx) => (
                    <CctvTile
                      key={`cctv-${pageIdx}-${page}`}
                      page={page}
                      pageIdx={pageIdx}
                      thumbnail={pageThumbnailsByPage.get(page)}
                      dots={pageDetectionDots.get(page) || []}
                      zoomTargets={pageZoomTargets.get(page) || []}
                      isProcessing={isUnifiedPowerCctv || currentProgress?.status === 'processing'}
                      hasDetections={hasAnyDetectionDots}
                      tone={previewTone}
                    />
                  ))}
                </div>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minHeight: 0, borderTop: '1px solid rgba(148, 163, 184, 0.18)' }}>
                  <div style={{ width: '50%', position: 'relative' }}>
                    <CctvTile
                      page={previewPages[2]}
                      pageIdx={2}
                      thumbnail={pageThumbnailsByPage.get(previewPages[2])}
                      dots={pageDetectionDots.get(previewPages[2]) || []}
                      zoomTargets={pageZoomTargets.get(previewPages[2]) || []}
                      isProcessing={isUnifiedPowerCctv || currentProgress?.status === 'processing'}
                      hasDetections={hasAnyDetectionDots}
                      tone={previewTone}
                    />
                  </div>
                </div>
              </div>
            )}
            {pageCount >= 4 && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gridTemplateRows: 'repeat(2, 1fr)',
                  gap: '0',
                  background: '#0b1220',
                }}
              >
                {previewPages.slice(0, 4).map((page, pageIdx) => (
                  <CctvTile
                    key={`cctv-${pageIdx}-${page}`}
                    page={page}
                    pageIdx={pageIdx}
                    thumbnail={pageThumbnailsByPage.get(page)}
                    dots={pageDetectionDots.get(page) || []}
                    zoomTargets={pageZoomTargets.get(page) || []}
                    isProcessing={isUnifiedPowerCctv || currentProgress?.status === 'processing'}
                    hasDetections={hasAnyDetectionDots}
                    tone={previewTone}
                  />
                ))}
              </div>
            )}

            {/* HUD overlay — status text on top */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              {/* Top HUD */}
              <div
                style={{
                  padding: '24px 28px 16px',
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.38) 70%, transparent 100%)',
                }}
              >
                {(() => {
                  let aggregatePercentage: number;
                  let aggregateHasDetections: boolean;
                  let aggregateStartedAt = 0;
                  let aggregateEstimatedTotal = 0;
                  let aggregateDetectionCount = 0;
                  let anyFailed = false;
                  let allComplete = true;

                  if (isUnifiedPowerCctv) {
                    let totalB = 0;
                    let completedB = 0;
                    let fallbackStartedAt = 0;
                    let fallbackMaxEstimatedTotal = 0;
                    let receptacleStartedAt = 0;
                    let receptacleEstimatedTotal = 0;
                    let hasReceptacleTimerSource = false;
                    const combinedPageSet = new Set<number>();
                    Array.from(componentPagesByName.values()).forEach((pages) => {
                      pages.forEach((rawPage: number) => {
                        const page = Math.floor(Number(rawPage));
                        if (!Number.isFinite(page) || page <= 0) return;
                        combinedPageSet.add(page);
                      });
                    });
                    multiComponentDetections.forEach((comp: any, idx: number) => {
                      if (comp.isLighting) return;
                      const prog = countAllComponentProgress[idx];
                      totalB += (prog?.totalBatches || 0);
                      completedB += (prog?.completedBatches || 0);
                      if (prog?.startedAt && (!fallbackStartedAt || prog.startedAt < fallbackStartedAt)) {
                        fallbackStartedAt = prog.startedAt;
                      }
                      fallbackMaxEstimatedTotal = Math.max(fallbackMaxEstimatedTotal, prog?.estimatedTotalSeconds || 0);
                      if (isReceptacleComponentName(comp?.componentName)) {
                        hasReceptacleTimerSource = true;
                        if (prog?.startedAt && (!receptacleStartedAt || prog.startedAt < receptacleStartedAt)) {
                          receptacleStartedAt = prog.startedAt;
                        }
                        receptacleEstimatedTotal = Math.max(receptacleEstimatedTotal, prog?.estimatedTotalSeconds || 0);
                      }
                      if (prog?.status === 'failed') anyFailed = true;
                      if (prog?.status !== 'complete') allComplete = false;
                      aggregateDetectionCount += (comp.detections?.length || 0);
                    });
                    const combinedPageCount = combinedPageSet.size;
                    const combinedPageBaseline = combinedPageCount > 0 ? estimatePageOnlySeconds(combinedPageCount) : 0;
                    if (hasReceptacleTimerSource && receptacleStartedAt > 0) {
                      aggregateStartedAt = receptacleStartedAt;
                      aggregateEstimatedTotal = Math.max(receptacleEstimatedTotal, combinedPageBaseline);
                    } else {
                      aggregateStartedAt = fallbackStartedAt;
                      const fallbackModeledEstimate = combinedPageBaseline;
                      aggregateEstimatedTotal = Math.max(fallbackModeledEstimate, fallbackMaxEstimatedTotal);
                    }
                    aggregatePercentage = totalB > 0 ? Math.round((completedB / totalB) * 100) : 0;
                    aggregateHasDetections = aggregateDetectionCount > 0;
                  } else {
                    const totalBatches = currentProgress ? (currentProgress.totalBatches || 0) : 0;
                    const completedBatches = currentProgress ? (currentProgress.completedBatches || 0) : 0;
                    aggregatePercentage = totalBatches > 0 ? Math.round((completedBatches / totalBatches) * 100) : 0;
                    aggregateHasDetections = !!(currentComponent && currentComponent.detections.length > 0);
                    aggregateStartedAt = currentProgress?.startedAt || 0;
                    aggregateEstimatedTotal = currentProgress?.estimatedTotalSeconds || 0;
                    aggregateDetectionCount = currentComponent?.detections?.length || 0;
                    anyFailed = currentProgress?.status === 'failed';
                    allComplete = currentProgress?.status === 'complete';
                  }

                  const percentage = aggregatePercentage;
                  const hasDetections = aggregateHasDetections;

                  let phaseLabel: string;
                  let phaseIcon: React.ReactNode = null;
                  if (anyFailed) {
                    phaseLabel = 'Something went wrong';
                    phaseIcon = <CircleAlert size={18} style={{ color: '#ef4444', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))' }} />;
                  } else if (allComplete || percentage >= 70) {
                    phaseLabel = 'Almost there...';
                    phaseIcon = (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Bot size={18} style={{ color: '#93c5fd', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))', animation: 'phaseNod 1s ease-in-out infinite' }} />
                        <ClipboardPen size={14} style={{ color: '#a5b4fc', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))', animation: 'phaseScribble 0.8s ease-in-out infinite' }} />
                      </span>
                    );
                  } else if (percentage >= 15 || (percentage === 0 && hasDetections)) {
                    phaseLabel = 'Scanning the blueprint';
                    phaseIcon = (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Bot size={18} style={{ color: '#93c5fd', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))', animation: 'phaseLook 3s ease-in-out infinite' }} />
                        <ScanSearch size={14} style={{ color: '#93c5fd', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))', animation: 'phaseScan 3s ease-in-out infinite' }} />
                      </span>
                    );
                  } else {
                    phaseLabel = 'Eyeballing';
                    phaseIcon = (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Bird size={18} style={{ color: '#fbbf24', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))', animation: 'phaseBounce 0.7s ease-in-out infinite' }} />
                        <Eye size={14} style={{ color: '#fbbf24', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))', animation: 'phaseLook 3s ease-in-out infinite' }} />
                      </span>
                    );
                  }

                  const elapsedSec = aggregateStartedAt > 0 ? Math.floor((now - aggregateStartedAt) / 1000) : 0;
                  const remainingSec = Math.max(0, aggregateEstimatedTotal - elapsedSec);
                  const mins = Math.floor(remainingSec / 60);
                  const secs = remainingSec % 60;
                  const showTimer = aggregateStartedAt > 0 && !allComplete && remainingSec > 0;

                  return (
                    <>
                      <h3 style={{ margin: 0, color: '#ffffff', fontSize: '20px', fontWeight: 700, textShadow: '0 1px 6px rgba(0,0,0,0.7)' }}>
                        {isLightingReview ? 'Scanning lighting devices' : 'Scanning power devices'}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                        {phaseIcon}
                        <span style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.92)', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                          {phaseLabel}
                        </span>
                        {showTimer && (
                          <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.72)', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                            · {mins}:{secs.toString().padStart(2, '0')} remaining
                          </span>
                        )}
                      </div>
                      {hasDetections && (
                        <div style={{ marginTop: '6px', fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.8)', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                          {aggregateDetectionCount} detections found so far
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Bottom HUD — back + cancel */}
              <div
                style={{
                  padding: '16px 28px 24px',
                  background: 'linear-gradient(0deg, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.24) 70%, transparent 100%)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pointerEvents: 'auto',
                }}
              >
                <div>
                  {currentReviewComponentIndex > 0 && (
                    <button
                      onClick={handleMultiComponentBack}
                      style={{
                        padding: '10px 20px',
                        background: 'rgba(255,255,255,0.14)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.28)',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <ArrowLeft size={14} />
                      Back
                    </button>
                  )}
                </div>
                {/* <button
                  onClick={handleCancel}
                  style={{
                    padding: '10px 20px',
                    background: 'rgba(255,255,255,0.14)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.28)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                  }}
                >
                  Cancel
                </button> */}
              </div>
            </div>

            {/* Keyframes for this mode */}
            <style>{`
              @keyframes miniScanDragHole {
                0% { transform: scale(0); opacity: 0.95; }
                60% { transform: scale(1.02); opacity: 1; }
                100% { transform: scale(1); opacity: 1; }
              }
              @keyframes miniScanPoof {
                0% { opacity: 1; transform: scale(1); filter: blur(0); }
                100% { opacity: 0; transform: scale(0.84); filter: blur(3px); }
              }
              @keyframes miniScanLabelPoof {
                0% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                100% { opacity: 0; transform: translate(-50%, -50%) scale(0.76); }
              }
              @keyframes miniScanLabelPulse {
                0%, 100% { transform: translate(-50%, -50%) scale(1); }
                50% { transform: translate(-50%, -50%) scale(1.06); }
              }
              @keyframes miniScanSweep {
                0%   { top: 2px; opacity: 0.8; }
                45%  { opacity: 1; }
                100% { top: calc(100% - 2px); opacity: 0.9; }
              }
              @keyframes miniScanFill {
                0%   { clip-path: inset(0 0 100% 0); opacity: 0.8; }
                100% { clip-path: inset(0 0 0% 0); opacity: 1; }
              }
              @keyframes miniThumbPulse {
                0%, 100% { opacity: 0.72; }
                50% { opacity: 1; }
              }
              @keyframes zoomShowcasePopIn {
                0% { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
                100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
              }
              @keyframes phaseBounce {
                0%, 100% { transform: translateY(0) scaleY(1); }
                50% { transform: translateY(3px) scaleY(0.85); }
              }
              @keyframes phaseLift {
                0%, 100% { transform: translateY(0) rotate(0deg); }
                50% { transform: translateY(-3px) rotate(-8deg); }
              }
              @keyframes phaseLook {
                0%, 100% { transform: rotate(0deg); }
                20% { transform: rotate(-10deg); }
                40% { transform: rotate(10deg); }
                60% { transform: rotate(-6deg); }
                80% { transform: rotate(8deg); }
              }
              @keyframes phaseScan {
                0%, 100% { transform: translateX(0) rotate(0deg); }
                20% { transform: translateX(-3px) rotate(-12deg); }
                40% { transform: translateX(2px) rotate(10deg); }
                60% { transform: translateX(-2px) rotate(-8deg); }
                80% { transform: translateX(3px) rotate(10deg); }
              }
              @keyframes phaseNod {
                0%, 100% { transform: translateX(0); }
                30% { transform: translateX(2px); }
                60% { transform: translateX(-2px); }
              }
              @keyframes phaseScribble {
                0%, 100% { transform: rotate(0deg); }
                25% { transform: rotate(4deg); }
                50% { transform: rotate(-4deg); }
                75% { transform: rotate(3deg); }
              }
              @keyframes phaseCelebrate {
                0%, 100% { transform: scale(1) rotate(0deg); }
                25% { transform: scale(1.15) rotate(-5deg); }
                50% { transform: scale(1.2) rotate(5deg); }
                75% { transform: scale(1.15) rotate(-3deg); }
              }
            `}</style>
          </>
        )}

        {/* ===== NORMAL REVIEW MODE (when not in fullbleed preview) ===== */}
        {!shouldShowFullBleedPreview && (
          <>
        <style>{`
          .review-clear-scroll {
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
          }
          .review-clear-scroll::-webkit-scrollbar {
            width: 10px;
          }
          .review-clear-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .review-clear-scroll::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.26);
            border-radius: 999px;
            border: 2px solid transparent;
            background-clip: padding-box;
          }
          .review-clear-scroll:hover::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.42);
            background-clip: padding-box;
          }
        `}</style>
        {/* Header */}
        {(() => {
          const isGroupedReceptacles = currentComponent?.componentName === '__RECEPTACLES_GROUP__';
          const isGroupedLighting = currentComponent?.componentName === '__LIGHTING_GROUP__';
          const isGroupedTeleData = currentComponent?.componentName === '__TELE_DATA_GROUP__';
          const headerDisplayName = isGroupedReceptacles ? 'RECEPTACLES' : isGroupedLighting ? 'LIGHTING' : isGroupedTeleData ? 'TELE/DATA OUTLETS' : currentComponent?.componentName;
          return (
            <div style={{ marginBottom: '20px', paddingBottom: '16px', padding: showLiveScanPreview ? '0' : undefined }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ margin: 0, color: '#ffffff', fontSize: '22px', fontWeight: 600 }}>
                  {isGroupedReceptacles
                    ? 'Review Receptacles'
                    : isGroupedLighting
                      ? 'Review Lighting Fixtures'
                      : isGroupedTeleData
                        ? 'Review Tele/Data Outlets'
                        : `Review ${headerDisplayName}`
                  }
                </h3>
                {/* {currentComponent && (
                  <div
                    style={{ position: 'relative' }}
                    onMouseEnter={() => { if (isGroupedReceptacles && receptacleGroupComponents.length > 0) setShowReceptacleRefPopup(true); }}
                    onMouseLeave={() => setShowReceptacleRefPopup(false)}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 16px 8px 8px',
                      background: currentComponent.componentColor,
                      borderRadius: '12px',
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '16px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      cursor: isGroupedReceptacles && receptacleGroupComponents.length > 0 ? 'pointer' : 'default'
                    }}>
                      {!isGroupedReceptacles && !isGroupedLighting && currentComponent.referenceImage && (
                        <img
                          src={currentComponent.referenceImage}
                          alt={headerDisplayName}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            background: 'var(--bg-primary)'
                          }}
                        />
                      )}
                      {headerDisplayName}
                      {isGroupedReceptacles && receptacleGroupComponents.length > 0 && (
                        <span style={{ fontSize: '12px', opacity: 0.85 }}>▾</span>
                      )}
                    </div>
                    {showReceptacleRefPopup && isGroupedReceptacles && receptacleGroupComponents.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '8px',
                        background: 'var(--bg-primary)',
                        borderRadius: '12px',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                        border: '1px solid var(--border-color)',
                        padding: '16px',
                        zIndex: 100010,
                        display: 'grid',
                        gridTemplateColumns: `repeat(${Math.min(receptacleGroupComponents.length, 4)}, 1fr)`,
                        gap: '16px',
                        minWidth: '280px',
                        maxWidth: '600px'
                      }}>
                        {receptacleGroupComponents.map((rc) => (
                          <div key={rc.name} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            {rc.referenceImage ? (
                              <img
                                src={rc.referenceImage}
                                alt={rc.name}
                                style={{
                                  width: '80px',
                                  height: '80px',
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                  border: `2px solid ${rc.color}`,
                                  background: 'var(--bg-secondary)'
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '8px',
                                background: rc.color + '22',
                                border: `2px solid ${rc.color}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: rc.color,
                                fontSize: '24px'
                              }}>
                                ⚡
                              </div>
                            )}
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              color: 'var(--text-secondary)',
                              textAlign: 'center',
                              lineHeight: '1.3',
                              maxWidth: '100px',
                              wordWrap: 'break-word'
                            }}>
                              {rc.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )} */}
              </div>

              <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.88)', fontSize: '14px', marginBottom: '12px' }}>
                {currentComponent
                  ? (isCountingAll && currentProgress?.status === 'processing'
                      ? ``
                      : isCountingAll && currentProgress?.status === 'failed'
                        ? (currentProgress.failMessage || `Counting failed for ${headerDisplayName}.`)
                        : isCountingAll && currentProgress?.status === 'pending'
                          ? `Queued ${headerDisplayName}...`
                          : currentComponent.detections.length === 0
                            ? `No detections found for ${headerDisplayName}. Continue to the next component.`
                            : `Found ${currentComponent.detections.length} detections. Click a card to approve or disapprove it. All detections are approved by default.`)
                  : 'Loading...'
                }
              </p>

              {/* Phase status — animated icon messages */}
              {isCountingAll && (() => {
                const totalBatches = currentProgress ? (currentProgress.totalBatches || 0) : 0;
                const completedBatches = currentProgress ? (currentProgress.completedBatches || 0) : 0;
                const percentage = totalBatches > 0 ? Math.round((completedBatches / totalBatches) * 100) : 0;
                const hasDetections = currentComponent && currentComponent.detections.length > 0;

                let phaseIcon: React.ReactNode;
                let phaseLabel: string;

                if (currentProgress?.status === 'failed') {
                  phaseLabel = 'Something went wrong';
                  phaseIcon = <CircleAlert size={20} style={{ color: '#ef4444', animation: 'phaseShake 0.8s ease-in-out infinite' }} />;
                } else if (currentProgress?.status === 'complete' || percentage >= 70) {
                  phaseLabel = 'Almost there...';
                  phaseIcon = (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Bot size={20} style={{ color: '#3b82f6', animation: 'phaseNod 1s ease-in-out infinite' }} />
                      <ClipboardPen size={16} style={{ color: '#6366f1', animation: 'phaseScribble 0.8s ease-in-out infinite' }} />
                    </span>
                  );
                } else if (percentage >= 15 || (percentage === 0 && hasDetections)) {
                  phaseLabel = 'Scanning the blueprint';
                  phaseIcon = (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Bot
                        size={20}
                        style={{ color: '#3b82f6', animation: 'phaseLook 3s ease-in-out infinite' }}
                      />
                      <ScanSearch
                        size={16}
                        style={{ color: '#3b82f6', animation: 'phaseScan 3s ease-in-out infinite' }}
                      />
                    </span>
                  );
                } else {
                  phaseLabel = 'Eyeballing';
                  phaseIcon = (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Bird size={20} style={{ color: '#fbbf24', animation: 'phaseBounce 0.7s ease-in-out infinite' }} />
                      <Eye size={16} style={{ color: '#fbbf24', animation: 'phaseLook 3s ease-in-out infinite' }} />
                    </span>
                  );
                }

                const startedAt = currentProgress?.startedAt || 0;
                const estimatedTotal = currentProgress?.estimatedTotalSeconds || 0;
                const elapsedSec = startedAt > 0 ? Math.floor((now - startedAt) / 1000) : 0;
                const remainingSec = Math.max(0, estimatedTotal - elapsedSec);
                const mins = Math.floor(remainingSec / 60);
                const secs = remainingSec % 60;
                const showTimer = startedAt > 0 && currentProgress?.status === 'processing' && remainingSec > 0;

                return (
                  <>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      {phaseIcon}
                      <span style={{ fontWeight: 600, color: '#ffffff', fontSize: '14px' }}>
                        {phaseLabel}
                      </span>
                      {showTimer && (
                        <span style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.78)', fontSize: '13px', marginLeft: '8px' }}>
                          Estimated time remaining: {mins}:{secs.toString().padStart(2, '0')}
                        </span>
                      )}
                    </span>
                    <style>{`
                      @keyframes phaseBounce {
                        0%, 100% { transform: translateY(0) scaleY(1); }
                        50% { transform: translateY(3px) scaleY(0.85); }
                      }
                      @keyframes phaseLift {
                        0%, 100% { transform: translateY(0) rotate(0deg); }
                        50% { transform: translateY(-3px) rotate(-8deg); }
                      }
                      @keyframes phaseLook {
                        0%, 100% { transform: rotate(0deg); }
                        20% { transform: rotate(-10deg); }
                        40% { transform: rotate(10deg); }
                        60% { transform: rotate(-6deg); }
                        80% { transform: rotate(8deg); }
                      }
                      @keyframes phaseScan {
                        0%, 100% { transform: translateX(0) rotate(0deg); }
                        20% { transform: translateX(-3px) rotate(-12deg); }
                        40% { transform: translateX(2px) rotate(10deg); }
                        60% { transform: translateX(-2px) rotate(-8deg); }
                        80% { transform: translateX(3px) rotate(10deg); }
                      }
                      @keyframes phaseNod {
                        0%, 100% { transform: translateX(0); }
                        30% { transform: translateX(2px); }
                        60% { transform: translateX(-2px); }
                      }
                      @keyframes phaseScribble {
                        0%, 100% { transform: rotate(0deg); }
                        25% { transform: rotate(4deg); }
                        50% { transform: rotate(-4deg); }
                        75% { transform: rotate(3deg); }
                      }
                      @keyframes phaseCelebrate {
                        0%, 100% { transform: scale(1) rotate(0deg); }
                        25% { transform: scale(1.15) rotate(-5deg); }
                        50% { transform: scale(1.2) rotate(5deg); }
                        75% { transform: scale(1.15) rotate(-3deg); }
                      }
                      @keyframes phaseShake {
                        0%, 100% { transform: rotate(0deg); }
                        25% { transform: rotate(12deg); }
                        50% { transform: rotate(-12deg); }
                        75% { transform: rotate(8deg); }
                      }
                      @keyframes miniScanDragHole {
                        0% { transform: scale(0); opacity: 0.95; }
                        60% { transform: scale(1.02); opacity: 1; }
                        100% { transform: scale(1); opacity: 1; }
                      }
                      @keyframes miniScanPoof {
                        0% { opacity: 1; transform: scale(1); filter: blur(0); }
                        100% { opacity: 0; transform: scale(0.84); filter: blur(3px); }
                      }
                      @keyframes miniScanLabelPoof {
                        0% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                        100% { opacity: 0; transform: translate(-50%, -50%) scale(0.76); }
                      }
                      @keyframes miniScanLabelPulse {
                        0%, 100% { transform: translate(-50%, -50%) scale(1); }
                        50% { transform: translate(-50%, -50%) scale(1.06); }
                      }
                      @keyframes miniScanSweep {
                        0%   { top: 2px; opacity: 0.8; }
                        45%  { opacity: 1; }
                        100% { top: calc(100% - 2px); opacity: 0.9; }
                      }
                      @keyframes miniScanFill {
                        0%   { clip-path: inset(0 0 100% 0); opacity: 0.8; }
                        100% { clip-path: inset(0 0 0% 0); opacity: 1; }
                      }
                      @keyframes miniThumbPulse {
                        0%, 100% { opacity: 0.72; }
                        50% { opacity: 1; }
                      }
                    `}</style>
                  </>
                );
              })()}

              {currentComponent && (
                <div style={{ display: 'flex', gap: '16px', fontSize: '14px', alignItems: 'center', width: '100%' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '999px',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '13px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.28)',
                    border: '1px solid rgba(255, 255, 255, 0.22)'
                  }}>
                    ✓ Approved: {currentSelections.size}
                  </span>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '999px',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '13px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.28)',
                    border: '1px solid rgba(255, 255, 255, 0.22)'
                  }}>
                    ✗ Disapproved: {currentComponent.detections.length - currentSelections.size}
                  </span>
                  <button
                    onClick={() => {
                      setAllComponentSelections((prev) => {
                        const next = new Map(prev);
                        next.set(currentComponent.componentName, new Set());
                        return next;
                      });
                    }}
                    disabled={isCountingAll}
                    style={{
                      marginLeft: 'auto',
                      padding: '6px 14px',
                      borderRadius: '999px',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#ef4444',
                      background: 'transparent',
                      border: '1.5px solid #ef4444',
                      cursor: isCountingAll ? 'wait' : 'pointer',
                      opacity: isCountingAll ? 0.5 : 1,
                    }}
                  >
                    Disapprove All
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* Scrollable Flat Grid for current component */}
        <div
          className="review-clear-scroll"
          style={{
            flex: 1,
            overflowY: 'auto',
            marginBottom: '20px',
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '14px',
            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.22)',
            padding: '10px',
          }}
        >
          {/* Flat grid of detections for current component */}
          {currentComponent && currentComponent.detections.length === 0 && (!isCountingAll || currentProgress?.status === 'complete' || currentProgress?.status === 'failed') && (
            <div
              style={{
                minHeight: '180px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: 'rgba(255, 255, 255, 0.82)',
                fontSize: '15px',
                fontWeight: 500,
                padding: '18px',
              }}
            >
              No detections found for this component.
            </div>
          )}
          {currentComponent && currentComponent.detections.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: '16px',
                padding: '4px'
              }}
            >
              {currentComponent.detections.map((det: any, idx: number) => {
                const isSelected = currentSelections.has(det.id);
                const detPage = det.prediction.page || pageNumber;
                const thumbnailScale = getLightingThumbnailScale(det.prediction, isLightingReview);

                return (
                  <div
                    key={det.id}
                    data-detection-id={det.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (e.shiftKey && currentComponent) {
                        const cardEl = e.currentTarget as HTMLDivElement;
                        const gridEl = cardEl.parentElement as HTMLElement | null;
                        if (gridEl) {
                          const rowTop = cardEl.offsetTop;
                          const rowDetectionIds = Array.from(gridEl.children)
                            .filter((child) => Math.abs((child as HTMLElement).offsetTop - rowTop) <= 2)
                            .map((child) => (child as HTMLElement).dataset.detectionId)
                            .filter((id): id is string => Boolean(id));

                          if (rowDetectionIds.length > 0) {
                            setAllComponentSelections((prev) => {
                              const next = new Map(prev);
                              const currentSet = new Set(next.get(currentComponent.componentName) || []);
                              rowDetectionIds.forEach((id) => currentSet.delete(id));
                              next.set(currentComponent.componentName, currentSet);
                              return next;
                            });
                            return;
                          }
                        }
                      }

                      toggleMultiDetectionSelection(currentComponent.componentName, det.id);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = isSelected ? `0 0 12px ${selectedBorderColor}40` : 'none';
                    }}
                    style={{
                      border: isSelected ? `3px solid ${selectedBorderColor}` : '3px solid var(--border-color)',
                      borderRadius: '10px',
                      padding: '8px',
                      cursor: isCountingAll ? 'wait' : 'pointer',
                      background: isSelected ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                      transition: 'all 0.2s',
                      opacity: isCountingAll ? 0.7 : (isSelected ? 1 : 0.6),
                      position: 'relative',
                      boxShadow: isSelected ? `0 0 12px ${selectedBorderColor}40` : 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none'
                    }}
                  >
                    {/* Approval checkbox */}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      zIndex: 3,
                      background: isSelected ? '#059669' : '#dc2626',
                      color: 'white',
                      borderRadius: '50%',
                      width: '26px',
                      height: '26px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '15px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
                      transition: 'background 0.2s',
                      border: '2px solid rgba(255, 255, 255, 0.9)'
                    }}>
                      {isSelected ? '✓' : '✗'}
                    </div>

                    {/* Page badge */}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      background: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 500,
                      transition: 'left 0.15s'
                    }}>
                      P{detPage}
                    </div>

                    {/* Thumbnail */}
                    <div style={{
                      overflow: 'hidden',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      aspectRatio: '4/3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <img
                        src={det.thumbnail}
                        alt={`Detection ${idx + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          transform: `scale(${thumbnailScale})`,
                          transformOrigin: 'center center',
                          background: 'var(--bg-primary)',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>

                    {/* Classification info */}
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
                      {(() => {
                        if (det.prediction.unfiltered && !det.prediction.classifiedName) {
                          return (
                            <div style={{
                              fontSize: '10px',
                              marginTop: '4px',
                              padding: '2px 6px',
                              background: 'var(--yellow-tint-medium)',
                              color: 'var(--yellow-text-dark)',
                              borderRadius: '4px',
                              fontWeight: 600,
                              lineHeight: '1.3',
                              animation: 'pulse 1.5s infinite'
                            }}>
                              Classifying...
                            </div>
                          );
                        }

                        const className = detectionClassifications.get(det.id) || det.prediction.classifiedName || '';
                        if (!className) return null;

                        const isReceptacleReview = currentComponent.componentName === '__RECEPTACLES_GROUP__';
                        const displayedClassName = isReceptacleReview
                          ? formatReceptacleClassLabel(className)
                          : className;

                        if (currentComponent.componentName === '__LIGHTING_GROUP__' && lightingGroupComponents.length > 0) {
                          const lcComp = lightingGroupComponents.find((lc) => lc.symbol.toUpperCase() === className);
                          return (
                            <div style={{
                              fontSize: '10px',
                              marginTop: '4px',
                              padding: '2px 6px',
                              background: 'var(--yellow-tint-medium)',
                              color: '#854d0e',
                              borderRadius: '4px',
                              fontWeight: 600,
                              lineHeight: '1.3'
                            }}>
                              {className}{lcComp ? ` — ${lcComp.name}` : ''}
                            </div>
                          );
                        }

                        return (
                          <div style={{
                            fontSize: '10px',
                            marginTop: '4px',
                            padding: '2px 6px',
                            background: 'var(--blue-tint-medium)',
                            color: 'var(--blue-text-dark)',
                            borderRadius: '4px',
                            fontWeight: 600,
                            lineHeight: '1.3'
                          }}>
                            {displayedClassName}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* <button
              onClick={handleCancel}
              disabled={isFinalizingReview}
              style={{
                padding: '4px 0',
                background: 'none',
                border: 'none',
                color: '#ffffff',
                fontSize: '15px',
                fontWeight: 500,
                cursor: isFinalizingReview ? 'not-allowed' : 'pointer',
                opacity: isFinalizingReview ? 0.5 : 1,
              }}
            >
              Cancel
            </button> */}

            {/* <button
              onClick={handleExportDatasetZip}
              disabled={isCountingAll || isFinalizingReview || isExportingDataset || totalDetectionsForExport === 0}
              style={{
                padding: '4px 0',
                background: 'none',
                border: 'none',
                color: '#ffffff',
                fontSize: '15px',
                fontWeight: 500,
                cursor: (isCountingAll || isFinalizingReview || isExportingDataset || totalDetectionsForExport === 0) ? 'not-allowed' : 'pointer',
                opacity: (isCountingAll || isFinalizingReview || isExportingDataset || totalDetectionsForExport === 0) ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {isExportingDataset ? 'Building ZIP...' : 'Export ZIP'}
            </button> */}

            {currentReviewComponentIndex > 0 && (
              <button
                onClick={handleMultiComponentBack}
                disabled={isFinalizingReview}
                style={{
                  padding: '6px 14px',
                  background: 'transparent',
                  border: '1.5px solid #ffffff',
                  borderRadius: '999px',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: isFinalizingReview ? 'not-allowed' : 'pointer',
                  opacity: isFinalizingReview ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <ArrowLeft size={15} />
                Back
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {(isLastComponent || multiComponentDetections.length <= 1) && totalApprovedCredits > 0 && (
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>
                Total: <span style={{ color: '#22c55e' }}>{totalApprovedCredits}</span> detections
              </span>
            )}

            {isLastComponent || multiComponentDetections.length <= 1 ? (
              <button
                onClick={handleFinishMultiComponentReview}
                disabled={isCountingAll || isFinalizingReview}
                style={{
                  padding: '10px 18px',
                  background: (isCountingAll || isFinalizingReview)
                    ? 'rgba(148, 163, 184, 0.45)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '10px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: (isCountingAll || isFinalizingReview) ? 'not-allowed' : 'pointer',
                  opacity: (isCountingAll || isFinalizingReview) ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: (isCountingAll || isFinalizingReview) ? 'none' : '0 6px 18px rgba(37, 99, 235, 0.35)',
                }}
              >
                <Rocket size={15} />
                {isFinalizingReview ? 'Saving...' : (isCountingAll ? 'Counting...' : 'Finish Counting')}
              </button>
            ) : (
              <button
                onClick={handleMultiComponentNext}
                disabled={(() => {
                  const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
                  const isCurrentStillCounting = currentProgress?.status === 'processing';
                  return isCurrentStillCounting || (isCountingAll && !isParallelCountSession);
                })()}
                style={{
                  padding: '10px 18px',
                  background: (() => {
                    const cp = countAllComponentProgress[currentReviewComponentIndex];
                    const still = cp?.status === 'processing';
                    return (still || (isCountingAll && !isParallelCountSession))
                      ? 'rgba(148, 163, 184, 0.45)'
                      : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                  })(),
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '10px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: (() => {
                    const cp = countAllComponentProgress[currentReviewComponentIndex];
                    const still = cp?.status === 'processing';
                    return (still || (isCountingAll && !isParallelCountSession)) ? 'not-allowed' : 'pointer';
                  })(),
                  opacity: (() => {
                    const cp = countAllComponentProgress[currentReviewComponentIndex];
                    const still = cp?.status === 'processing';
                    return (still || (isCountingAll && !isParallelCountSession)) ? 0.5 : 1;
                  })(),
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  textShadow: (() => {
                    const cp = countAllComponentProgress[currentReviewComponentIndex];
                    const still = cp?.status === 'processing';
                    return (still || (isCountingAll && !isParallelCountSession))
                      ? 'none'
                      : '0 1px 2px rgba(0, 0, 0, 0.25)';
                  })(),
                  boxShadow: (() => {
                    const cp = countAllComponentProgress[currentReviewComponentIndex];
                    const still = cp?.status === 'processing';
                    return (still || (isCountingAll && !isParallelCountSession))
                      ? 'none'
                      : '0 6px 18px rgba(37, 99, 235, 0.35)';
                  })(),
                }}
              >
                {(() => {
                  const cp = countAllComponentProgress[currentReviewComponentIndex];
                  const still = cp?.status === 'processing';
                  if (still) return 'Counting...';
                  if (isCountingAll && !isParallelCountSession) return 'Counting...';
                  if (pendingKeyComponents.length > 0) {
                    return (
                      <>
                        Next ({pendingKeyComponents.length} left)
                        <ArrowRight size={14} />
                      </>
                    );
                  }
                  const nextComponentName = (() => {
                    const raw = multiComponentDetections[currentReviewComponentIndex + 1]?.componentName;
                    if (raw === '__RECEPTACLES_GROUP__') return 'receptacles';
                    if (raw === '__LIGHTING_GROUP__') return 'lighting';
                    if (raw === '__TELE_DATA_GROUP__') return 'tele/data';
                    return (raw || 'next').toLowerCase();
                  })();
                  return (
                    <>
                      {`Review ${nextComponentName}`}
                      <ArrowRight size={14} />
                    </>
                  );
                })()}
              </button>
            )}
          </div>
          {reviewFinalizeError && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#ef4444' }}>
              {reviewFinalizeError}
            </div>
          )}
          {datasetExportError && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#ef4444' }}>
              {datasetExportError}
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};
